/**
 * SecureEmbed Energy Portal - Persona Sub-Role Definitions
 *
 * 12 energy-sector sub-roles across 4 functional groups mapped to portal pages.
 * Used by the SubRoleSelector component for persona-driven navigation.
 */

export interface SubRoleDefinition {
  key: string;
  label: string;
  description: string;
  iconName: string;
  color: string;
  bgColor: string;
  /** Portal routes this sub-role primarily uses */
  primaryPages: string[];
}

export interface SubRoleGroup {
  key: string;
  label: string;
  description: string;
  color: string;
  subRoles: string[];
}

// ---------------------------------------------------------------------------
// 4 Sub-Role Groups
// ---------------------------------------------------------------------------
export const SUB_ROLE_GROUPS: SubRoleGroup[] = [
  {
    key: 'market-operations',
    label: 'Market Operations',
    description: 'Analysts and traders working with AEMO spot markets, forecasting, and settlements',
    color: '#7C3AED',
    subRoles: ['forecasting-analyst', 'spot-market-trader', 'settlements-analyst'],
  },
  {
    key: 'grid-infrastructure',
    label: 'Grid & Infrastructure',
    description: 'Engineers and planners managing grid connections, network capacity, and REZ development',
    color: '#0284C7',
    subRoles: ['grid-operator', 'network-planner', 'connection-engineer'],
  },
  {
    key: 'regulatory-compliance',
    label: 'Regulatory & Compliance',
    description: 'Officers and advisors ensuring compliance with AER rules, NER obligations, and energy policy',
    color: '#DC2626',
    subRoles: ['compliance-officer', 'regulatory-analyst', 'policy-advisor'],
  },
  {
    key: 'sustainability-esg',
    label: 'Sustainability & ESG',
    description: 'Specialists tracking carbon markets, ESG reporting, and renewable energy sustainability metrics',
    color: '#059669',
    subRoles: ['sustainability-officer', 'carbon-analyst', 'esg-reporter'],
  },
];

// ---------------------------------------------------------------------------
// 12 Sub-Role Definitions
// ---------------------------------------------------------------------------
export const SUB_ROLES: Record<string, SubRoleDefinition> = {
  // --- Group 1: Market Operations ---
  'forecasting-analyst': {
    key: 'forecasting-analyst',
    label: 'Forecasting Analyst',
    description: 'Analyses AEMO demand and price forecasts to support generation scheduling and hedging strategies.',
    iconName: 'TrendingUp',
    color: '#7C3AED',
    bgColor: '#F5F3FF',
    primaryPages: ['/', '/search', '/insights', '/reports', '/export'],
  },
  'spot-market-trader': {
    key: 'spot-market-trader',
    label: 'Spot Market Trader',
    description: 'Monitors real-time NEM spot prices and FCAS markets to optimise bidding and dispatch decisions.',
    iconName: 'Activity',
    color: '#6D28D9',
    bgColor: '#F5F3FF',
    primaryPages: ['/', '/search', '/streaming', '/export'],
  },
  'settlements-analyst': {
    key: 'settlements-analyst',
    label: 'Settlements Analyst',
    description: 'Reconciles AEMO settlement data, validates metering, and tracks revenue from energy dispatch.',
    iconName: 'Calculator',
    color: '#5B21B6',
    bgColor: '#F5F3FF',
    primaryPages: ['/', '/search', '/reports', '/export', '/provenance'],
  },

  // --- Group 2: Grid & Infrastructure ---
  'grid-operator': {
    key: 'grid-operator',
    label: 'Grid Operator',
    description: 'Monitors grid stability, network constraints, and manages real-time operational decisions.',
    iconName: 'Zap',
    color: '#0284C7',
    bgColor: '#F0F9FF',
    primaryPages: ['/', '/search', '/streaming', '/insights'],
  },
  'network-planner': {
    key: 'network-planner',
    label: 'Network Planner',
    description: 'Plans network augmentation, assesses REZ capacity, and models future grid infrastructure needs.',
    iconName: 'Network',
    color: '#0369A1',
    bgColor: '#F0F9FF',
    primaryPages: ['/', '/search', '/insights', '/reports', '/partners'],
  },
  'connection-engineer': {
    key: 'connection-engineer',
    label: 'Connection Engineer',
    description: 'Manages grid connection queue applications and technical assessments for new generation projects.',
    iconName: 'GitBranch',
    color: '#075985',
    bgColor: '#F0F9FF',
    primaryPages: ['/', '/search', '/partners', '/provenance'],
  },

  // --- Group 3: Regulatory & Compliance ---
  'compliance-officer': {
    key: 'compliance-officer',
    label: 'Compliance Officer',
    description: 'Ensures organisational compliance with NER, NERL, and AER regulatory determinations.',
    iconName: 'ShieldCheck',
    color: '#DC2626',
    bgColor: '#FEF2F2',
    primaryPages: ['/', '/search', '/reports', '/provenance', '/research'],
  },
  'regulatory-analyst': {
    key: 'regulatory-analyst',
    label: 'Regulatory Analyst',
    description: 'Analyses AER rule changes, regulatory proposals, and their impact on energy market participants.',
    iconName: 'FileText',
    color: '#B91C1C',
    bgColor: '#FEF2F2',
    primaryPages: ['/', '/search', '/insights', '/research', '/export'],
  },
  'policy-advisor': {
    key: 'policy-advisor',
    label: 'Policy Advisor',
    description: 'Advises on energy policy developments, government consultations, and market reform impacts.',
    iconName: 'BookOpen',
    color: '#991B1B',
    bgColor: '#FEF2F2',
    primaryPages: ['/', '/search', '/insights', '/research', '/partners'],
  },

  // --- Group 4: Sustainability & ESG ---
  'sustainability-officer': {
    key: 'sustainability-officer',
    label: 'Sustainability Officer',
    description: 'Leads sustainability strategy, tracks renewable energy targets, and manages environmental commitments.',
    iconName: 'Leaf',
    color: '#059669',
    bgColor: '#ECFDF5',
    primaryPages: ['/', '/search', '/insights', '/reports', '/partners'],
  },
  'carbon-analyst': {
    key: 'carbon-analyst',
    label: 'Carbon Analyst',
    description: 'Monitors carbon markets, tracks emissions data, and models abatement pathways for energy assets.',
    iconName: 'BarChart3',
    color: '#047857',
    bgColor: '#ECFDF5',
    primaryPages: ['/', '/search', '/export', '/reports', '/research'],
  },
  'esg-reporter': {
    key: 'esg-reporter',
    label: 'ESG Reporter',
    description: 'Prepares ESG disclosures, sustainability reports, and tracks performance against ESG frameworks.',
    iconName: 'ClipboardList',
    color: '#065F46',
    bgColor: '#ECFDF5',
    primaryPages: ['/', '/search', '/reports', '/export', '/provenance'],
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
export const SUB_ROLE_LIST = Object.values(SUB_ROLES);

export const getSubRole = (key: string): SubRoleDefinition | undefined => SUB_ROLES[key];

export const getSubRolesByGroup = (groupKey: string): SubRoleDefinition[] => {
  const group = SUB_ROLE_GROUPS.find((g) => g.key === groupKey);
  if (!group) return [];
  return group.subRoles.map((k) => SUB_ROLES[k]).filter(Boolean);
};
